import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { PUBLIC_BASE_URL, HEADERS } from '../utils/config';
import { setToken } from '../hooks/auth';
import { AuthDataTypes } from '../types/types';
import { AppSnackbar } from '../hooks/appSnackbar';

interface InitialStateTypes {
  loading: boolean;
  data: AuthDataTypes | null;
  error: any;
}

interface AuthDataType {
  email?: string;
  password?: string;
  password_confirmation?: string;
  token?: string;
}

type AuthActionType = 'login' | 'reset-password' | 'forgot-password' | 'verify-token';

const initialState: InitialStateTypes = {
  loading: false,
  data: null,
  error: null,
};

export const authAction = createAsyncThunk(
  'auth/authAction',
  async (
    { actionType, method, data }: { actionType: AuthActionType; method: string; data: AuthDataType },
    { rejectWithValue }
  ) => {
    let url = '';

    switch (actionType) {
      case 'login':
        url = `${PUBLIC_BASE_URL}/auth/login`;
        break;
      case 'reset-password':
        url = `${PUBLIC_BASE_URL}/auth/reset-password`;
        break;
      case 'forgot-password':
        url = `${PUBLIC_BASE_URL}/auth/forgot-password`;
        break;
      case 'verify-token':
        if (!data.token) throw new Error('Token is required for verify-token');
        url = `${PUBLIC_BASE_URL}/auth/verify-password-token/${data.token}`;
        break;
      default:
        throw new Error('Invalid action type');
    }

    try {
      const config = {
        method: method,
        url: url,
        headers: await HEADERS(),
        data: data,
      };
      const response = await axios(config);

      if (actionType === 'login') {
        setToken(response.data.data);
        AppSnackbar({ type: 'success', message: "Successfully logged in" });
      } else if (actionType === 'reset-password') {
        AppSnackbar({ type: 'success', message: "Password reset successfully" });
      } else if (actionType === 'forgot-password') {
        AppSnackbar({ type: 'success', message: "Password reset link sent to your email" });
      } else if (actionType === 'verify-token') {
        AppSnackbar({ type: 'success', message: "Token verified successfully" });
      }

      return response.data.data;
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        AppSnackbar({ type: 'error', message: error.response.data.message });
      } else if (error.code === "ERR_BAD_REQUEST") {
        AppSnackbar({ type: 'error', message: 'Network error!' });
      }
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(authAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(authAction.fulfilled, (state, action: PayloadAction<AuthDataTypes>) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(authAction.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default authSlice.reducer;
